import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, ChevronLeft, ChevronRight, Copy, Drumstick, MapPin, ShieldCheck, Snowflake, Star, Truck } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import ToneImg from "@/components/storefront/ToneImg";
import { Reveal } from "@/components/pharmacy/hooks";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { M, MeatCard, MeatRow, MTitle, Stars, imgOf, packText, priceText, needsOptions } from "@/components/meat/MeatParts";
import { homeSets, discountPct } from "@/lib/homesets";
import { freeOver } from "@/lib/storenav";
import { inr } from "@/lib/storeutils";

const wrap = "max-w-[1320px] mx-auto px-4 md:px-5";
const uniq = (list) => { const seen = new Set(); return list.filter((p) => p && !seen.has(p.id) && seen.add(p.id)); };

/** Days / hours / minutes / seconds until the end of this week (Sunday night). */
function useWeekCountdown() {
  const calc = () => {
    const now = new Date(); const end = new Date(now);
    end.setDate(now.getDate() + ((7 - now.getDay()) % 7)); end.setHours(23, 59, 59, 999);
    const t = Math.max(0, Math.floor((end - now) / 1000));
    return { d: Math.floor(t / 86400), h: Math.floor((t % 86400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
  };
  const [left, setLeft] = useState(calc);
  useEffect(() => { const id = setInterval(() => setLeft(calc()), 1000); return () => clearInterval(id); }, []);
  return left;
}

const SAMPLE_REVIEWS = [
  ["Rohan M.", "Super fresh and cleanly cut. Delivery was on time and the packaging kept everything cold. Our go-to now."],
  ["Priya S.", "Quality is consistently great. The weekend deals are a bonus — ordering every week."],
  ["Imran K.", "Genuinely fresh, no smell, exactly the weight promised. Highly recommended."],
];

export default function MeatHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  const all = s.products;
  const sets = useMemo(() => homeSets(all, { fresh: 8, featured: 8, sale: 8 }), [all]);
  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-red-100 border-t-red-600 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const { slug, store } = s;
  if (q) {
    const shown = all.filter((p) => `${p.name} ${p.category || ""}`.toLowerCase().includes(q));
    return (
      <StoreShell>
        <section id="shop" className={`${wrap} py-10`} data-testid="meat-search-results">
          <MTitle>Results for “{q}”</MTitle>
          {shown.length === 0 ? <div className="text-center py-16 text-slate-500">No products found.</div> : (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="storefront-products">{shown.map((p, k) => <MeatCard key={p.id} p={p} s={s} cart={cart} k={k} />)}</div>
          )}
        </section>
      </StoreShell>
    );
  }

  const withImg = all.filter((p) => imgOf(p));
  // the shelves of homeSets never share products, so the vendor's flagged (featured) products are taken straight from the catalogue
  const flagged = all.filter((p) => p.featured).sort((x, y) => String(y.created_at || "").localeCompare(String(x.created_at || "")));
  const featuredList = uniq([...flagged, ...sets.featured]);
  const lead = uniq([...featuredList, ...withImg]);
  const pick = (i) => lead[i % Math.max(lead.length, 1)];
  const cats = s.categories.map((name) => ({ name, count: all.filter((p) => p.category === name).length, image: imgOf(all.find((p) => p.category === name && imgOf(p))) }));
  const maxOff = all.reduce((m, p) => Math.max(m, discountPct(p)), 0);
  const dealPool = uniq([...sets.sale, ...sets.featured, ...all]).slice(0, 7);
  const dealStar = [...dealPool].filter((p) => imgOf(p)).sort((x, y) => y.price - x.price)[0];       // the premium cut leads the Deal of the Week
  const deals = dealStar ? [dealStar, ...dealPool.filter((p) => p.id !== dealStar.id)] : dealPool;
  const onSellPool = uniq([...sets.sale, ...all.filter((p) => discountPct(p) > 0), ...all]).slice(0, 4);
  const bigOne = [...onSellPool].filter((p) => imgOf(p)).sort((x, y) => (y.weight || 0) - (x.weight || 0))[0];           // the big On Sell card is the largest pack
  const onSell = bigOne ? [bigOne, ...onSellPool.filter((p) => p.id !== bigOne.id)] : onSellPool;
  const best = uniq([...featuredList, ...sets.fresh, ...all]).slice(0, 4);
  const trending = uniq([...sets.fresh, ...all]).slice(0, 8);
  // the four promo tiles: one product from the chicken, mutton, seafood and egg categories (like the design), else the next products
  const tilePick = (re) => { const c = cats.find((x) => re.test(x.name)); const list = all.filter((p) => c && p.category === c.name && imgOf(p)); return list.find((p) => /curry/i.test(p.name)) || list.find((p) => p.featured) || list[0]; };
  const tileItems = uniq([tilePick(/chick/i), tilePick(/mutton|lamb|goat/i), tilePick(/sea|fish/i), tilePick(/egg/i), pick(4), pick(5), pick(6), pick(7)]).slice(0, 4);
  const coupon = s.coupons?.[0];
  const offerProduct = uniq([...featuredList.filter((p) => discountPct(p) > 0), ...sets.sale, ...all])[0];

  return (
    <StoreShell>
      <Hero s={s} items={[pick(0), pick(1), pick(2)]} special={[...flagged].filter((p) => imgOf(p)).sort((x, y) => y.price - x.price)[0] || pick(3)} cats={cats} maxOff={maxOff} />
      <TrustStrip store={store} />
      <Featured s={s} cart={cart} items={uniq([...featuredList, ...all]).slice(0, 8)} />
      {offerProduct && (coupon || maxOff > 0) && <OfferBanner s={s} p={offerProduct} coupon={coupon} maxOff={maxOff} />}
      {cats.length > 0 && <TopCategories s={s} cats={cats} />}
      {deals.length > 2 && <Deal s={s} cart={cart} deals={deals} lovers={cats[0]?.name} />}
      <PromoTiles s={s} items={tileItems} />
      {trending.length > 0 && (
        <section id="new-arrivals" className={`${wrap} py-8`}>
          <Reveal><MTitle to={`/store/${slug}/shop`}>Trending Products</MTitle></Reveal>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="storefront-products">
            {trending.map((p, k) => <Reveal key={p.id} delay={(k % 4) * 80} kind="rv-zoom"><MeatRow p={p} s={s} cart={cart} /></Reveal>)}
          </div>
        </section>
      )}
      <DailySpecials s={s} p={pick(8)} cat={cats[0]?.name} maxOff={maxOff} />
      <section id="bestsellers" className={`${wrap} py-8`}>
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-x-6 gap-y-8">
          <div>
            <Reveal><MTitle to={`/store/${slug}/shop?sort=price-asc`} more="View More">On Sell Products</MTitle></Reveal>
            <div className="mt-5 grid md:grid-cols-[1fr_1fr] gap-4">
              {onSell[0] && <Reveal kind="rv-left"><BigCard p={onSell[0]} s={s} cart={cart} /></Reveal>}
              <div className="grid gap-4 content-start">{onSell.slice(1, 4).map((p, k) => <Reveal key={p.id} delay={k * 90} kind="rv-zoom"><MeatRow p={p} s={s} cart={cart} /></Reveal>)}</div>
            </div>
          </div>
          <div>
            <Reveal><MTitle to={`/store/${slug}/shop`} more="View More">Best Seller</MTitle></Reveal>
            <div className="mt-5 grid gap-4">{best.map((p, k) => <Reveal key={p.id} delay={k * 90} kind="rv-right"><MeatRow p={p} s={s} cart={cart} /></Reveal>)}</div>
          </div>
        </div>
      </section>
      <ManagedReviews s={s} layout="market" fallback={<SampleReviews />} />
      <ManagedInstagram s={s} layout="market" />
    </StoreShell>
  );
}

/* -------------------------------------------------------------------- hero */
function Hero({ s, items, special, cats, maxOff }) {
  const { slug, store } = s;
  const main = items.filter(Boolean);
  const names = cats.slice(0, 4).map((c) => c.name);
  const tagline = store.tagline && !/^welcome to /i.test(store.tagline) ? store.tagline : "";      // the platform default tagline is not a headline
  const heroImg = s.theme?.hero_image;
  const m = tagline.match(/^Fresh (.+?) Delivered to Your Doorstep$/i);
  const lines = m ? ["Fresh", m[1], "Delivered to", "Your Doorstep"] : null;
  // The two category cards: seafood and eggs when the store has them, otherwise its first two categories.
  const find = (re) => cats.find((c) => re.test(c.name));
  const chosen = [find(/sea|fish/i), find(/egg/i)].filter(Boolean);
  const pair = [...new Map([...chosen, ...cats].map((c) => [c.name, c])).values()].slice(0, 2);
  const COPY = [[/sea|fish/i, "Fresh Seafood", "From the Ocean"], [/egg/i, "Farm Fresh Eggs", "Protein for Every Meal"], [/chick/i, "Fresh Chicken", "Tender & Juicy"], [/mutton|lamb|goat/i, "Premium Mutton", "Fresh Cuts Daily"]];
  const copyOf = (c) => { const hit = COPY.find(([re]) => re.test(c.name)); return hit ? [hit[1], hit[2]] : [`Fresh ${c.name}`, `${c.count} item${c.count === 1 ? "" : "s"}`]; };
  return (
    <section className={`${wrap} pt-5`} data-testid="meat-hero">
      <div className="grid lg:grid-cols-[1.58fr_1fr] gap-4">
        <div className="mv-hero relative overflow-hidden rounded-[22px] text-white min-h-[340px] md:min-h-[410px]">
          <span className="mv-spark" style={{ left: "8%", top: "18%" }} /><span className="mv-spark" style={{ left: "46%", top: "10%", animationDelay: "1.2s" }} /><span className="mv-spark" style={{ left: "38%", top: "78%", animationDelay: "2.1s" }} />
          {heroImg && <div className="mv-hero-img hidden md:block absolute right-0 top-0 bottom-0 w-[52%]"><img src={heroImg} alt="" className="w-full h-full object-cover" style={{ maskImage: "linear-gradient(90deg,transparent,#000 22%)", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 22%)" }} /></div>}
          <div className="relative z-10 p-7 md:p-10 md:max-w-[56%]">
            <span className="mv-rise mv-shimmer inline-flex items-center rounded-md px-3.5 py-1.5 text-[12px] font-bold" style={{ background: M.red }}>Premium {store.business_category || "Fresh"} Store</span>
            <h1 className="mv-rise mt-5 text-[32px] md:text-[clamp(28px,3.1vw,44px)] leading-[1.05] font-extrabold" style={{ animationDelay: ".12s" }}>
              {lines ? lines.map((l, i) => <span key={i} className="block">{l}</span>) : (tagline || <>Fresh <span style={{ color: "#ffd166" }}>{names[0] || "Food"}</span> Delivered to Your Doorstep</>)}
            </h1>
            {names.length > 0 && <p className="mv-rise mt-5 text-[13px] md:text-[14px]" style={{ color: "#d3ddf0", animationDelay: ".24s" }}>{names.join(" · ")}</p>}
            <Link to={`/store/${slug}/shop`} data-testid="meat-hero-cta" className="mv-rise mv-cta mv-ring mt-7 inline-flex items-center gap-2 h-12 px-8 rounded-lg text-[14px] font-bold text-white" style={{ background: M.red, animationDelay: ".36s" }}>Shop Now <ArrowRight size={16} /></Link>
          </div>
          {!heroImg && (
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-[46%] pointer-events-none">
              {main[0] && <div className="mv-float absolute right-[6%] top-[8%] w-[56%] aspect-square rounded-[2rem] overflow-hidden border-4 border-white/90 shadow-2xl" style={{ "--r": "3deg" }}><ToneImg src={imgOf(main[0])} alt="" /></div>}
              {main[1] && <div className="mv-float absolute left-[2%] bottom-[8%] w-[40%] aspect-square rounded-3xl overflow-hidden border-4 border-white/90 shadow-2xl" style={{ animationDelay: "1s", "--r": "-6deg" }}><ToneImg src={imgOf(main[1])} alt="" /></div>}
              {main[2] && <div className="mv-float absolute right-[2%] bottom-[2%] w-[34%] aspect-square rounded-3xl overflow-hidden border-4 border-white/90 shadow-2xl" style={{ animationDelay: "2s", "--r": "6deg" }}><ToneImg src={imgOf(main[2])} alt="" /></div>}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 grid-rows-[1.2fr_1fr] gap-4">
          {special && (
            <Link to={`/store/${slug}/product/${special.id}`} className="mv-tile col-span-2 relative overflow-hidden rounded-[18px] pl-6 pr-4 py-4 flex items-center justify-between gap-3 min-h-[150px]" style={{ background: "linear-gradient(120deg,#eaf3ff,#d6e7ff)" }} data-testid="meat-special">
              <div className="relative z-10 min-w-0">
                <div className="text-[12px] font-extrabold" style={{ color: M.red }}>Today&apos;s Special</div>
                <div className="mt-1.5 text-[20px] md:text-[22px] font-extrabold leading-[1.1] line-clamp-2" style={{ color: M.ink }}>{special.name}</div>
                <div className="mt-2 flex items-baseline gap-2.5"><span className="text-[20px] font-extrabold" style={{ color: M.red }}>{priceText(special)}</span>{special.compare_at_price && !needsOptions(special) && <span className="text-[13px] line-through text-slate-400">{inr(special.compare_at_price)}</span>}</div>
                <span className="mv-pill mt-3 inline-flex items-center gap-1.5 h-8 px-4 rounded-md text-[12px] font-bold text-white" style={{ background: M.navy }}>Shop Now <ArrowRight size={12} /></span>
              </div>
              <div className="mv-float mix-blend-multiply shrink-0 w-[46%] aspect-[4/3]"><ToneImg src={imgOf(special)} alt="" /></div>
            </Link>
          )}
          {pair.map((c, i) => { const [title, sub] = copyOf(c); const blue = i === 0; return (
            <Link key={c.name} to={`/store/${slug}/shop?category=${encodeURIComponent(c.name)}`} className="mv-tile relative overflow-hidden rounded-[18px] p-4 min-h-[140px] flex flex-col justify-between"
              style={{ background: blue ? "linear-gradient(135deg,#1f73e6,#1456c0)" : "linear-gradient(135deg,#ffeadb,#ffd9bd)", color: blue ? "#fff" : M.ink }} data-testid={`meat-cat-card-${i}`}>
              <div className="relative z-10 max-w-[60%]">
                <div className="text-[16px] font-extrabold leading-[1.1] line-clamp-2">{title}</div>
                <div className="text-[11px] mt-1 opacity-85 leading-tight">{sub}</div>
                <span className="mv-pill mt-3 inline-flex items-center gap-1 h-7 px-3 rounded-md text-[10.5px] font-bold whitespace-nowrap" style={{ background: blue ? "#fff" : M.red, color: blue ? "#1456c0" : "#fff" }}>Shop Now <ArrowRight size={10} /></span>
              </div>
              {c.image && <div className={`mv-float absolute ${blue ? "right-2 bottom-2 w-[38%] aspect-square rounded-xl overflow-hidden shadow-lg" : "mix-blend-multiply -right-2 bottom-0 w-[44%] aspect-square"}`} style={{ animationDelay: `${i * 0.8}s` }}><ToneImg src={c.image} alt="" /></div>}
              {!blue && maxOff > 0 && <span className="mv-spin-pulse absolute right-3 bottom-3 w-[62px] h-[62px] rounded-full flex flex-col items-center justify-center text-white text-[15px] font-extrabold leading-none" style={{ background: M.red }}>{maxOff}%<span className="text-[10px] mt-0.5">OFF</span></span>}
            </Link>
          ); })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ trust strip */
function TrustStrip({ store }) {
  const free = freeOver(store);
  const items = [
    [MapPin, "Fresh & Hygienic", "Quality Checked"], [Snowflake, "Cold Chain Delivery", "Maintains Freshness"],
    [Truck, free > 0 ? "Free Delivery" : "Reliable Delivery", free > 0 ? `On Orders Above ₹${free.toLocaleString("en-IN")}` : "Tracked, courier-delivered"], [ShieldCheck, "Secure Payments", "100% Safe & Easy"],
  ];
  return (
    <section className={`${wrap} pt-4`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 rounded-2xl border lg:divide-x lg:divide-[#dde3ee]" style={{ borderColor: "#e3e8f1", background: "#f5f7fb" }}>
        {items.map(([I, h, d], k) => (
          <Reveal key={h} delay={k * 80} kind="rv-zoom" className="mv-trust flex items-center gap-3.5 px-6 py-[18px] justify-center lg:justify-start">
            <span className="mv-trust-ic shrink-0" style={{ color: M.red }}><I size={28} strokeWidth={1.6} /></span>
            <div><div className="text-[13.5px] font-extrabold" style={{ color: M.ink }}>{h}</div><div className="text-[11.5px]" style={{ color: M.muted }}>{d}</div></div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- featured */
function Featured({ s, cart, items }) {
  const rail = useRef(null);
  const [paused, setPaused] = useState(false);
  const go = (d) => { const el = rail.current; if (!el) return; const step = el.clientWidth * 0.5; if (d > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) el.scrollTo({ left: 0, behavior: "smooth" }); else el.scrollBy({ left: d * step, behavior: "smooth" }); };
  useEffect(() => { if (paused || items.length < 6) return undefined; const id = setInterval(() => go(1), 3800); return () => clearInterval(id); }, [paused, items.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return null;
  return (
    <section id="featured" className={`${wrap} pt-8 pb-2`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <Reveal>
        <MTitle to={`/store/${s.slug}/shop`} right={<div className="flex gap-1.5">
          <button type="button" onClick={() => go(-1)} aria-label="Previous products" className="mv-arrow-btn w-8 h-8 rounded-full border flex items-center justify-center bg-white" style={{ borderColor: M.line }}><ChevronLeft size={15} /></button>
          <button type="button" onClick={() => go(1)} aria-label="Next products" className="mv-arrow-btn w-8 h-8 rounded-full border flex items-center justify-center bg-white" style={{ borderColor: M.line }}><ChevronRight size={15} /></button></div>}>Featured Products</MTitle>
      </Reveal>
      <div ref={rail} className="mt-5 flex gap-4 overflow-x-auto scroll-smooth pb-3" style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none" }} data-testid="storefront-products">
        {items.map((p, k) => <Reveal key={p.id} delay={(k % 5) * 70} kind="rv-zoom" className="shrink-0 w-[200px] sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(20%-12.8px)]" style={{ scrollSnapAlign: "start" }}><MeatCard p={p} s={s} cart={cart} k={k} /></Reveal>)}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ offer banner */
function OfferBanner({ s, p, coupon, maxOff }) {
  const code = coupon?.code;
  const copy = () => { if (!code) return; try { navigator.clipboard?.writeText(code); } catch { /* clipboard unavailable */ } toast.success(`Code ${code} copied`); };
  const off = coupon ? (coupon.discount_type === "percent" ? `${coupon.value}% OFF` : `₹${coupon.value} OFF`) : `Up to ${maxOff}% OFF`;
  return (
    <section className={`${wrap} py-6`}>
      <Reveal kind="rv-zoom">
        <div className="mv-offer relative overflow-hidden rounded-2xl px-5 md:px-9 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid="meat-offer">
          <div className="flex items-center gap-4 min-w-0">
            {imgOf(p) && <div className="mv-float hidden sm:block w-[88px] h-[64px] rounded-xl overflow-hidden shrink-0"><ToneImg src={imgOf(p)} alt="" /></div>}
            <div className="min-w-0"><div className="text-[11px] font-bold" style={{ color: M.red }}>This Week&apos;s Special Offer</div>
              <Link to={`/store/${s.slug}/product/${p.id}`} className="block text-[20px] md:text-[26px] font-extrabold leading-tight truncate" style={{ color: M.ink }}>{p.name}</Link></div>
          </div>
          <div className="relative z-10 flex items-center gap-4 lg:mr-[14%]">
            <div className="text-right leading-tight"><div className="text-[13px] font-extrabold" style={{ color: M.ink }}>{coupon ? "Flat " : ""}<span style={{ color: M.red }}>{off}</span></div>{code && <div className="text-[10px]" style={{ color: M.muted }}>Use Code:</div>}</div>
            {code && <button type="button" onClick={copy} className="mv-ticket inline-flex items-center gap-2 h-10 px-5 rounded-lg text-[13px] font-extrabold tracking-wide text-white" style={{ background: M.navy }} data-testid="meat-coupon">{code}<Copy size={13} /></button>}
          </div>
          {imgOf(p) && <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-[16%] opacity-90 pointer-events-none" style={{ maskImage: "linear-gradient(90deg,transparent,#000 40%)", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 40%)" }}><ToneImg src={imgOf(p)} alt="" /></div>}
        </div>
      </Reveal>
    </section>
  );
}

/* ------------------------------------------------------------- categories */
function TopCategories({ s, cats }) {
  return (
    <section className={`${wrap} py-6`}>
      <Reveal><MTitle to={`/store/${s.slug}/shop`}>Top Categories</MTitle></Reveal>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cats.slice(0, 5).map((c, k) => (
          <Reveal key={c.name} delay={k * 80} kind="rv-zoom">
            <Link to={`/store/${s.slug}/shop?category=${encodeURIComponent(c.name)}`} data-testid={`meat-cat-${c.name}`} className="mv-cat group block rounded-2xl border bg-white overflow-hidden text-center" style={{ borderColor: M.line }}>
              <div className="aspect-[5/3.4] overflow-hidden" style={{ background: M.soft }}>{c.image && <ToneImg src={c.image} alt={c.name} loading="lazy" imgClassName="mv-cat-img" />}</div>
              <div className="py-3 text-[13px] font-extrabold" style={{ color: M.ink }}>{c.name}</div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- deal */
function Flip({ v, label }) {
  const text = String(v).padStart(2, "0");
  return (
    <div className="w-[52px] md:w-[58px] rounded-lg py-1.5 text-center text-white" style={{ background: M.red }}>
      <div key={text} className="mv-flip text-[20px] md:text-[22px] font-extrabold leading-none">{text}</div>
      <div className="text-[9px] opacity-85 mt-0.5">{label}</div>
    </div>
  );
}

function Deal({ s, cart, deals, lovers }) {
  const left = useWeekCountdown();
  const [hero, ...rest] = deals;
  const dealImg = s.theme?.deal_image;
  return (
    <section id="deals" className={`${wrap} py-6`} data-testid="meat-deal">
      <Reveal kind="rv-zoom">
        <div className="mv-deal rounded-2xl border p-4 md:p-5" style={{ borderColor: "#f1b4ba" }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-[20px] md:text-[26px] font-extrabold" style={{ color: M.ink }}>Deal Of The Week</h2>
            <div className="flex gap-2" aria-label="Time left" data-testid="meat-countdown"><Flip v={left.d} label="Days" /><Flip v={left.h} label="Hrs" /><Flip v={left.m} label="Min" /><Flip v={left.s} label="Sec" /></div>
          </div>
          <div className="mt-4 grid lg:grid-cols-[1.12fr_1fr] gap-4">
            <div className="grid sm:grid-cols-[1.65fr_1fr] gap-3">
              <Link to={`/store/${s.slug}/shop`} className="mv-banner relative overflow-hidden rounded-xl min-h-[250px] p-5 md:p-6 flex flex-col justify-center" style={{ background: "linear-gradient(120deg,#fbe9ea,#f4c2c6)" }}>
                {dealImg && <img src={dealImg} alt="" className="absolute right-0 top-0 h-full w-[62%] object-cover mv-zoom" style={{ maskImage: "linear-gradient(90deg,transparent,#000 30%)", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 30%)" }} />}
                <span className="mv-shimmer relative z-10 self-start rounded-md px-3 py-1.5 text-[11px] font-bold text-white" style={{ background: M.red }}>Limited Time Offer</span>
                <div className="relative z-10 mt-4 text-[22px] md:text-[clamp(20px,2.3vw,32px)] leading-[1.08] font-extrabold max-w-[62%]" style={{ color: M.ink }}><span className="block">Fresh Deals,</span><span className="block">Picked for</span><span className="block">{lovers ? lovers : "Food"} Lovers</span></div>
                {!dealImg && imgOf(hero) && <div className="mv-float absolute -right-6 bottom-0 w-[52%] aspect-square rounded-3xl overflow-hidden opacity-95" style={{ "--r": "6deg" }}><ToneImg src={imgOf(hero)} alt="" /></div>}
              </Link>
              {hero && (
                <div className="mv-card rounded-xl border bg-white p-3 flex flex-col" style={{ borderColor: M.line }}>
                  <Link to={`/store/${s.slug}/product/${hero.id}`} className="block rounded-lg overflow-hidden aspect-[5/4]" style={{ background: M.soft }}>{imgOf(hero) && <ToneImg src={imgOf(hero)} alt={hero.name} imgClassName="mv-zoom" />}</Link>
                  <h3 className="mt-2 text-[15px] font-extrabold line-clamp-2" style={{ color: M.ink }}>{hero.name}</h3>
                  <div className="text-[11px]" style={{ color: M.muted }}>{packText(hero)}</div>
                  <Stars id={hero.id} className="mt-1" />
                  <div className="mt-1 flex items-baseline gap-2"><span className="text-[18px] font-extrabold" style={{ color: M.ink }}>{priceText(hero)}</span>{hero.compare_at_price && !needsOptions(hero) && <span className="text-[11px] line-through text-slate-400">{inr(hero.compare_at_price)}</span>}</div>
                  <Link to={`/store/${s.slug}/product/${hero.id}`} className="mv-viewall mt-auto pt-2 inline-flex items-center gap-1 text-[12.5px] font-extrabold" style={{ color: M.green }}>Shop Now <ArrowRight size={13} className="mv-arrow" /></Link>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 content-start">{rest.slice(0, 6).map((p, k) => <Reveal key={p.id} delay={k * 70} kind="rv-zoom"><MeatRow p={p} s={s} cart={cart} /></Reveal>)}</div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ------------------------------------------------------------ promo tiles */
const TILE_BG = ["linear-gradient(120deg,#3b82f0,#5a9bf6)", "linear-gradient(120deg,#cdb4f3,#e3d2fa)", "linear-gradient(120deg,#35c3d6,#78dde2)", "linear-gradient(120deg,#fcd64c,#fee98f)"];
function PromoTiles({ s, items }) {
  const list = items.filter(Boolean);
  if (list.length < 2) return null;
  return (
    <section className={`${wrap} py-4`}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {list.slice(0, 4).map((p, k) => (
          <Reveal key={p.id + k} delay={k * 90} kind="rv-zoom">
            <Link to={`/store/${s.slug}/product/${p.id}`} className="mv-tile mv-grad relative overflow-hidden rounded-2xl pl-5 pr-3 py-4 min-h-[118px] flex items-center justify-between gap-2" style={{ background: TILE_BG[k % 4], color: k === 0 ? "#fff" : M.ink }} data-testid={`meat-promo-${k}`}>
              <div className="relative z-10 min-w-0">
                <div className="text-[15px] font-extrabold leading-tight line-clamp-2">{p.name}</div>
                <div className="text-[24px] font-extrabold mt-0.5">{priceText(p)}</div>
                <span className="mv-pill mt-2 inline-flex items-center gap-1.5 h-8 px-4 rounded-full text-[11.5px] font-bold bg-white" style={{ color: M.ink }}>Shop Now <ArrowRight size={11} /></span>
              </div>
              <div className="mv-float shrink-0 w-[42%] aspect-[4/3] rounded-xl overflow-hidden shadow-lg" style={{ animationDelay: `${k * 0.6}s` }}><ToneImg src={imgOf(p)} alt="" /></div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* --------------------------------------------------------- daily specials */
function DailySpecials({ s, p, cat, maxOff }) {
  const dailyImg = s.theme?.daily_image;
  if (!p && !dailyImg) return null;
  const off = maxOff >= 5 ? maxOff : 15;
  return (
    <section className={`${wrap} py-4`}>
      <Reveal kind="rv-zoom">
        <Link to={`/store/${s.slug}/shop`} className="mv-banner mv-dark relative overflow-hidden rounded-2xl flex items-center justify-between gap-4 px-6 md:px-14 min-h-[120px] md:min-h-[150px]" data-testid="meat-daily">
          {dailyImg && <img src={dailyImg} alt="" className="absolute right-0 top-0 h-full w-[62%] object-cover mv-zoom" style={{ maskImage: "linear-gradient(90deg,transparent,#000 35%)", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 35%)" }} />}
          <span className="mv-spark" style={{ left: "30%", top: "20%" }} /><span className="mv-spark" style={{ left: "62%", top: "70%", animationDelay: "1.5s" }} />
          <div className="relative z-10 flex items-center gap-4 md:gap-10 flex-wrap">
            <span className="text-[18px] md:text-[26px] font-extrabold text-white">Daily Specials</span>
            <span className="mv-shimmer-text text-[22px] md:text-[42px] leading-none" style={{ fontFamily: M.script, color: "#ffd166" }}>{cat ? `${cat} Feast Every Day!` : "Fresh Feast Every Day!"}</span>
          </div>
          <div className="relative z-10 flex items-center gap-4 md:gap-8">
            {!dailyImg && p && imgOf(p) && <div className="mv-float hidden sm:block w-[150px] md:w-[240px] aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl" style={{ "--r": "2deg" }}><ToneImg src={imgOf(p)} alt="" /></div>}
            <span className="mv-spin-pulse shrink-0 w-[74px] h-[74px] md:w-[96px] md:h-[96px] rounded-full flex flex-col items-center justify-center text-white font-extrabold leading-none" style={{ background: M.red }}><span className="text-[9px] md:text-[11px] font-bold">Up to</span><span className="text-[22px] md:text-[30px]">{off}%</span><span className="text-[10px] md:text-[13px]">OFF</span></span>
          </div>
        </Link>
      </Reveal>
    </section>
  );
}

/* ---------------------------------------------------------------- big card */
function BigCard({ p, s, cart }) {
  const hasOptions = needsOptions(p);
  const add = (e) => { e.preventDefault(); if (hasOptions) { window.location.href = `/store/${s.slug}/product/${p.id}`; return; } cart.add(p, null, 1); toast.success("Added to cart"); };
  return (
    <div className="mv-card group h-full rounded-xl border bg-white p-3 flex flex-col" style={{ borderColor: M.line }} data-testid={`storefront-product-${p.id}`}>
      <Link to={`/store/${s.slug}/product/${p.id}`} className="block rounded-lg overflow-hidden aspect-[4/3]" style={{ background: M.soft }}>{imgOf(p) && <ToneImg src={imgOf(p)} alt={p.name} imgClassName="mv-zoom" />}</Link>
      <h3 className="mt-3 text-[14px] font-extrabold line-clamp-2" style={{ color: M.ink }}>{p.name}</h3>
      <div className="text-[10.5px]" style={{ color: M.muted }}>{packText(p)}</div>
      <Stars id={p.id} className="mt-1" />
      <div className="mt-1 flex items-baseline gap-2"><span className="text-[17px] font-extrabold" style={{ color: M.ink }}>{priceText(p)}</span>{p.compare_at_price && !hasOptions && <span className="text-[11px] line-through text-slate-400">{inr(p.compare_at_price)}</span>}</div>
      <button type="button" onClick={add} disabled={p.stock <= 0} data-testid={`storefront-add-to-cart-btn-${p.id}`} className="mv-btn mt-auto mt-3 h-10 rounded-lg text-[12px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: M.greenSoft, color: M.green }}>{hasOptions ? "Select Options" : "Add to Cart"}</button>
    </div>
  );
}

function SampleReviews() {
  return (
    <section id="reviews" className={`${wrap} py-10`}>
      <Reveal><MTitle>Loved by Our Customers</MTitle></Reveal>
      <div className="mt-5 grid md:grid-cols-3 gap-4">
        {SAMPLE_REVIEWS.map(([name, text], k) => (
          <Reveal key={name} delay={k * 90} kind="rv-zoom">
            <div className="mv-card h-full rounded-2xl border bg-white p-5" style={{ borderColor: M.line }}>
              <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={14} fill="#f5a524" color="#f5a524" />)}</div>
              <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: M.muted }}>{text}</p>
              <div className="mt-3 flex items-center gap-2 text-[13px] font-extrabold" style={{ color: M.ink }}><BadgeCheck size={15} style={{ color: M.green }} />{name}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export { Drumstick };
